import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../auth';
import { APIVersion } from '../../constants/keys';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { MemberApi, MembershipApi } from '../../types/memberships';

const transformErrorResponse = (response: FetchBaseQueryError) =>
  (response as { data?: { errors?: unknown } }).data?.errors;

interface MembershipArgs {
  ordering?: string;
  page?: number;
  per_page?: number;
}

interface MembershipResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MembershipApi[];
}

// module for workspaces BE integration
export const membershipsApi = createApi({
  reducerPath: 'membershipsApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    // workspace-memberships
    getWorkspaceMemberships: builder.query<MembershipApi[], MembershipArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/workspace-memberships`,
        params,
      }),
      transformResponse: (data: MembershipResponse) => data.results,
    }),
    deleteWorkspaceMembership: builder.mutation<void, { workspaceMembershipId: string }>({
      query: ({ workspaceMembershipId }) => ({
        url: `/core-api/${APIVersion}/workspace-memberships/${workspaceMembershipId}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),
    updateRoleWorkspaceMembership: builder.mutation<MembershipApi, { workspaceMembershipId: string; role: string }>({
      query: ({ workspaceMembershipId, role }) => ({
        url: `/core-api/${APIVersion}/workspace-memberships/${workspaceMembershipId}`,
        method: 'PATCH',
        body: { role },
      }),
    }),

    // invitations
    addWorkspaceMembers: builder.mutation<MemberApi, { id: string; members: { email: string; role: string }[] }>({
      query: ({ id, members }) => ({
        url: `/core-api/${APIVersion}/workspaces/${id}/invitations`,
        method: 'POST',
        body: members,
      }),
      transformErrorResponse,
    }),
    getWorkspaceInvitations: builder.query<MembershipApi[], MembershipArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/workspace-invitations`,
        params,
      }),
      transformResponse: (data: MembershipResponse) => data.results,
    }),
    updateWorkspaceInvitation: builder.mutation<MemberApi, { invitationId: string; role: string }>({
      query: ({ invitationId, role }) => ({
        url: `/core-api/${APIVersion}/workspace-invitations/${invitationId}`,
        method: 'PATCH',
        body: { role },
      }),
      transformErrorResponse,
    }),
    deleteWorkspaceInvitation: builder.mutation<void, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: `/core-api/${APIVersion}/workspace-invitations/${invitationId}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),
  }),
});

export const {
  useAddWorkspaceMembersMutation,
  useGetWorkspaceMembershipsQuery,
  useDeleteWorkspaceMembershipMutation,
  useUpdateRoleWorkspaceMembershipMutation,

  useGetWorkspaceInvitationsQuery,
  useUpdateWorkspaceInvitationMutation,
  useDeleteWorkspaceInvitationMutation,
} = membershipsApi;
